/**
 * React hook for sync status
 *
 * Usage:
 * const { isOnline, isSyncing, pendingChanges, failedChanges, authHealth, syncNow, dataVersion } = useSync();
 *
 * The `dataVersion` number increments whenever sync brings in new data,
 * allowing components to trigger refetch when it changes.
 */

import { useState, useEffect, useCallback } from 'react';
import { syncEngine } from '../sync/SyncEngine';
import { onAuthHealthChange, forceCheckAuthHealth, type AuthHealthStatus } from '../lib/supabase';

interface SyncStatus {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncAt: Date | null;
  pendingChanges: number;
  failedChanges: number;
  oldestPending: Date | null;
  authHealth: AuthHealthStatus;
}

export function useSync() {
  const [status, setStatus] = useState<SyncStatus>({
    isOnline: navigator.onLine,
    isSyncing: false,
    lastSyncAt: null,
    pendingChanges: 0,
    failedChanges: 0,
    oldestPending: null,
    authHealth: 'checking'
  });

  // Data version increments whenever sync brings in new data
  // Components can use this to trigger refetch
  const [dataVersion, setDataVersion] = useState(0);

  const handleDataChange = useCallback(() => {
    setDataVersion(v => v + 1);
  }, []);

  useEffect(() => {
    // Get initial status
    syncEngine.getStatus().then(setStatus);

    // Subscribe to sync status changes
    const unsubscribeSyncStatus = syncEngine.onStatusChange(setStatus);

    // Subscribe to data change notifications
    const unsubscribeDataChange = syncEngine.onDataChange(handleDataChange);

    // Subscribe to auth health changes
    const unsubscribeAuthHealth = onAuthHealthChange((authHealth) => {
      setStatus(prev => ({ ...prev, authHealth }));
    });

    // Listen for online/offline events
    const handleOnline = () => {
      setStatus(prev => ({ ...prev, isOnline: true }));
      syncEngine.syncNow();
    };

    const handleOffline = () => {
      setStatus(prev => ({ ...prev, isOnline: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Also listen for browser event (alternative notification method)
    const handleBrowserDataChange = () => handleDataChange();
    window.addEventListener('pkm-data-changed', handleBrowserDataChange);

    return () => {
      unsubscribeSyncStatus();
      unsubscribeDataChange();
      unsubscribeAuthHealth();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('pkm-data-changed', handleBrowserDataChange);
    };
  }, [handleDataChange]);

  return {
    ...status,
    dataVersion,
    syncNow: () => syncEngine.syncNow(),
    clearAllPending: () => syncEngine.clearAllPending(),
    clearFailedEntries: () => syncEngine.clearFailedEntries(),
    retryFailedEntries: () => syncEngine.retryFailedEntries(),
    checkAuthHealth: () => forceCheckAuthHealth(),
    // Legacy method for backwards compatibility
    clearFailedQueue: () => syncEngine.clearFailedSyncQueue()
  };
}
